import { Module, MutationAction, VuexModule } from 'vuex-module-decorators';
import {
  AUTOMATIC_LOGOUT_AFTER_SECONDS,
  IUser,
  LoginDto,
  LoginMagicLinkDto,
  UpdateAccountSettingsDto,
  UpdateAchievementDto,
  UpdateUserDto,
  UserRoleType,
  UserStatus,
} from 'admiral-core';
import { AuthApi } from '@/services/AuthApi';
import { UserApi } from '@/services/UserApi';

export enum AuthAction {
  Login = 'login',
  LoginWithMagicLink = 'loginWithMagicLink',
  Logout = 'logout',
  UpdateUser = 'UpdateUser',
  UpdateAccountSettings = 'UpdateAccountSettings',
  UpdateAchievements = 'UpdateAchievements',
  RemoveProfilePicture = 'RemoveProfilePicture',
  SetProfilePicture = 'SetProfilePicture',
  UpdateLastApiCall = 'updatelastApiCall',
}

export interface AuthState {
  user: IUser;
  lastApiCall: Date;
}

@Module({ namespaced: true, name: 'AuthModule' })
export class AuthModule extends VuexModule<AuthState> {
  user: IUser | null = null;
  lastApiCall: Date | null = null;

  get isAuthenticated(): boolean {
    return this.user !== null;
  }

  get isSuperAdmin(): boolean {
    return this.user?.role.type === UserRoleType.SUPER_ADMIN;
  }

  get getLastApiCall(): Date | null {
    return this.lastApiCall;
  }
  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.Login](credentials: LoginDto) {
    const response = await AuthApi.login({
      email: credentials.email,
      password: credentials.password,
    });
    // @ts-ignore
    await this.dispatch(`${AuthAction.UpdateLastApiCall}`);
    return { user: response.data };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.LoginWithMagicLink](dto: LoginMagicLinkDto) {
    const response = await AuthApi.loginWithMagicLink(dto);
    // @ts-ignore
    await this.dispatch(`${AuthAction.UpdateLastApiCall}`);
    return { user: response.data };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.UpdateUser](id: string, dto: UpdateUserDto) {
    const response = await UserApi.update(id, dto);
    return { user: response.data };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.UpdateAccountSettings](data: UpdateAccountSettingsDto) {
    const response = await UserApi.updateAccountSettings(data.user.id, data);
    return { user: response.data };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.UpdateAchievements](dto: UpdateAchievementDto) {
    const user = (this.state as AuthState).user;
    await UserApi.updateAchievement(user.id, dto);
    // TODO: move this to backend
    if (!user.achievementMap) {
      user.achievementMap = {};
    }
    user.achievementMap[dto.item] = new Date();
    if (dto.item === 'login1') {
      user.status = UserStatus.ACTIVE;
    }
    return { user };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.RemoveProfilePicture](id: string) {
    await UserApi.removeProfilePicture(id);
    const user = (this.state as AuthState).user;
    user.profilePictureId = undefined;
    return { user };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.SetProfilePicture](data: { user: Partial<IUser>; formData: FormData }) {
    const response = await UserApi.setProfilePicture(data.user.id!, data.formData);
    const user = (this.state as AuthState).user;
    user.profilePictureId = response.data.profilePictureId;
    return { user };
  }

  @MutationAction({ mutate: ['user'], rawError: true })
  async [AuthAction.Logout]() {
    return { user: null };
  }

  @MutationAction({ mutate: ['lastApiCall'], rawError: true })
  async [AuthAction.UpdateLastApiCall]() {
    return { lastApiCall: new Date(new Date().getTime() + AUTOMATIC_LOGOUT_AFTER_SECONDS * 1000) };
  }
}
